import { useEffect, useRef, useState } from 'react'
import { isDevelopment } from '~/utils/misc'
import PlayToPause from '../../icon/play-to-pause'
import { cn } from '~/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'

const blockStyles = cva('', {
  variants: {
    btnPosition: {
      'top-right': 'top-s right-s',
      'top-left': 'top-s left-s',
      'bottom-right': 'bottom-s right-s',
      'bottom-left': 'bottom-s left-s '
    }
  },
  defaultVariants: {
    btnPosition: 'bottom-right'
  }
})

interface BlockVideoProps {
  videoUrl: string
  width: string
  height: string
  image: Image
  btnPosition?: VariantProps<typeof blockStyles>['btnPosition']
  className?: string
  btnClassName?: string
  pause?: boolean
  onLoad?: () => void
  inView?: boolean
}

const BlockVideo = (props: BlockVideoProps) => {
  const {
    videoUrl,
    width,
    height,
    image,
    className,
    btnPosition,
    btnClassName,
    pause,
    onLoad,
    inView = true
  } = props

  const [userPause, setUserPause] = useState(false)
  const vidRef = useRef<HTMLVideoElement>(null)
  const playToPauseRef = useRef<{ toggleAnimation: () => void }>(null)

  useEffect(() => {
    setUserPause(false)
  }, [pause])

  useEffect(() => {
    if (pause !== undefined && !userPause && inView) {
      videoAutoPause(pause)
    }
  }, [pause, inView, userPause])

  const videoAutoPause = (pause: boolean) => {
    if (vidRef.current) {
      const isPaused = vidRef.current.paused
      if (!pause && isPaused) {
        vidRef.current.play().catch(() => {
          // Ignore play errors
        })
      } else if (pause && !isPaused) {
        vidRef.current.pause()
      }
    }
  }

  const videoButtonPause = () => {
    if (vidRef.current) {
      const isPaused = vidRef.current.paused
      if (isPaused) {
        vidRef.current.play().catch(() => {
          // Ignore play errors
        })
        setUserPause(false)
      } else {
        vidRef.current.pause()
        setUserPause(true)
      }

      // Trigger the animation on the SVG icon
      if (playToPauseRef.current) {
        playToPauseRef.current.toggleAnimation()
      }
    }
  }

  // Video data
  const hostVideo =
    isDevelopment && !videoUrl.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com' + videoUrl
      : videoUrl

  // Image data
  const hostImage =
    isDevelopment && !image?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  const imgUrl = image?.asset.sys.uri
  const transformations = null

  const smImg = `${transformations ? '&w=' + (Number(width) / 3) * 1 : ''}`

  const imgFormat =
    transformations || imgUrl.includes('?') ? '&f=webp' : '?f=webp'

  return (
    <>
      <video
        ref={vidRef}
        className={cn('h-full w-full object-cover', className)}
        src={inView ? hostVideo : undefined}
        data-src={hostVideo}
        width={width}
        height={height}
        preload='none'
        loop
        muted
        playsInline
        autoPlay
        poster={`${hostImage}${imgUrl}${smImg}${imgFormat}`}
        onLoadStart={onLoad}
      />

      <button
        type='button'
        className={cn(
          blockStyles({ btnPosition }),
          btnClassName,
          'lg:p-2xs-plus p-2xs text-blue-d absolute z-2 flex cursor-pointer items-center justify-center rounded-full bg-white'
        )}
        onClick={videoButtonPause}
      >
        <PlayToPause
          ref={playToPauseRef}
          fill={''}
          secondaryfill={undefined}
          title={''}
          width={22}
          height={22}
        />
      </button>
    </>
  )
}

export default BlockVideo
